import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useNonExchangeDayService from '../../app/services/hooks/useNonExchangeDayService';
import { NonExchangeDay, NonExchangeDayFilters } from '../../app/models/NonExchangeDay';
import NonExchangeDayDataTable from './components/tables/NonExchangeDayDataTable';
import NonExchangeDayCreate from './components/NonExchangeDayCreate';
import NonExchangeDayEdit from './components/NonExchangeDayEdit';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'días de no cambio',
        url: '/non-exchange-days',
        isActive: true
    }
];

const NonExchangeDays = () => {
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetNonExchangeDays, getNonExchangeDays, deleteNonExchangeDay, activeNonExchangeDay } = useNonExchangeDayService();
    const [nonExchangeDays, setNonExchangeDays] = useState<NonExchangeDay[]>([]);

    const [nonExchangeDayIdEdit, setNonExchangeDayIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [nonExchangeDayFilter, setNonExchangeDayFilter] = useState<NonExchangeDayFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [nonExchangeDayFilter]);

    const getAllNonExchangeDays = () => {
        getNonExchangeDays(nonExchangeDayFilter, {
            onSuccess: (response: ServiceResponse) => {
                setNonExchangeDays(response.data.non_exchange_days);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (nonExchangeDayId: number) => {
        setShowingEdit(true);
        setNonExchangeDayIdEdit(nonExchangeDayId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setNonExchangeDayIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllNonExchangeDays();
    };

    const destroy = (nonExchangeDayId: number) => {
        const _text = 'Está a punto de eliminar el día de no cambio #' + nonExchangeDayId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando día de no cambio...');
                        deleteNonExchangeDay(nonExchangeDayId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El día de no cambio no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="días de no cambio" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={
                        <ButtonCreate callbackCreate={showCreate} title="Nuevo día de no cambio" />
                    }
                />
                <NonExchangeDayDataTable
                    nonExchangeDays={nonExchangeDays}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetNonExchangeDays}
                    filter={nonExchangeDayFilter}
                    setFilter={setNonExchangeDayFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear día de no cambio"
                    backdrop={true}
                    showFooter={false}
                >
                    <NonExchangeDayCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit && nonExchangeDayIdEdit > 0 ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar día de no cambio"
                    backdrop={true}
                    showFooter={false}
                >
                    <NonExchangeDayEdit
                        nonExchangeDayId={nonExchangeDayIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default NonExchangeDays; 